import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListingRow } from '@marstein-radar/shared';
import { useAuth } from '../auth/AuthContext';
import { buttonStyle, formatEur } from '../styles';

const CATEGORY_LABEL: Record<string, string> = {
  nehnutelnosti: 'Nehnuteľnosti',
  stroje_vybavenie: 'Stroje a vybavenie',
  zasoby_tovar: 'Zásoby/tovar',
  firmy_podiely: 'Firmy/podiely',
  vozidla: 'Vozidlá',
  ostatne: 'Ostatné',
};

const STATUS_LABEL: Record<string, string> = {
  pending_review: 'Čaká na schválenie',
  published: 'Zverejnený',
  rejected: 'Zamietnutý',
  sold: 'Predané',
};

// Fáza 2 inzertnej sekcie (2026-07-15) — vlastné inzeráty vo všetkých stavoch
// (rozdiel oproti verejnému /inzeraty feedu, ktorý ukazuje len status=published) —
// zamietnutie teda nie je pre postera tiché (viď listings.service.ts).
export function MyListingsPage() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();

  const { data: listings } = useQuery<ListingRow[]>({
    queryKey: ['listings-mine'],
    queryFn: async () => {
      const res = await fetch('/api/v1/listings/mine');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!user,
  });

  if (loading) return null;
  if (!user) return <Navigate to="/prihlasenie" replace />;

  async function markSold(id: string): Promise<void> {
    await fetch(`/api/v1/listings/${id}/mark-sold`, { method: 'POST' });
    await qc.invalidateQueries({ queryKey: ['listings-mine'] });
  }

  return (
    <main className="page">
      <div className="page-header">
        <h1>Moje inzeráty</h1>
      </div>

      {listings && listings.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Zatiaľ nemáš žiadne inzeráty. <a href="/inzeraty/pridat">Pridaj prvý</a>.
        </div>
      )}

      {listings && listings.length > 0 && (
        <div className="listing-grid">
          {listings.map((l) => (
            <div key={l.id} className="listing-card" style={{ cursor: 'default' }}>
              {l.images.length > 0 ? (
                <img
                  className="listing-card-image"
                  src={`/uploads/${l.images[0]!.filename}`}
                  alt={l.title}
                />
              ) : (
                <div className="listing-card-image-placeholder">Bez fotky</div>
              )}
              <div className="listing-card-body">
                <span className={`listing-status-badge listing-status-${l.status}`}>
                  {STATUS_LABEL[l.status] ?? l.status}
                </span>
                <div className="listing-card-meta">{CATEGORY_LABEL[l.category] ?? l.category}</div>
                <div>{l.title}</div>
                <div className="listing-card-price">
                  {l.priceCents != null ? formatEur(l.priceCents / 100) : 'Cena dohodou'}
                </div>
                {l.status === 'rejected' && l.rejectionReason && (
                  <div className="listing-card-meta" style={{ color: '#991b1b' }}>
                    Dôvod: {l.rejectionReason}
                  </div>
                )}
                {(l.status === 'pending_review' || l.status === 'published') && (
                  <button
                    type="button"
                    onClick={() => markSold(l.id)}
                    style={{ ...buttonStyle, marginTop: 8 }}
                  >
                    Označiť ako predané
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
